// Brief B4 §2 — MaIA admin diagnostic page.
//
// Renders the GET /api/admin/maia/diagnostic/{userId} payload in a digestible
// form for the operator: user header, completeness, context preview, the 4
// MaIA suggestions, last 24h LLM errors, chat volume.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldAlert, Loader2, ArrowLeft, AlertTriangle, Sparkles, Activity,
  MessageSquare,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMaiaDiagnostic = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const { data: who } = await axios.get(`${API}/admin/me/is-admin`, { withCredentials: true });
        if (!who.is_admin) {
          setAuthorized(false);
          setTimeout(() => navigate('/app/home', { replace: true }), 1200);
          return;
        }
        setAuthorized(true);
        const { data: diag } = await axios.get(`${API}/admin/maia/diagnostic/${userId}`, { withCredentials: true });
        setData(diag);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        toast.error(err?.response?.data?.detail || 'Erreur de chargement du diagnostic');
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-diag-loading">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }

  if (authorized === false) {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="admin-diag-forbidden">
        <ShieldAlert className="h-10 w-10 mx-auto text-red-500 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Accès réservé</h2>
      </div>
    );
  }

  if (!data) return null;
  const { user, maia_context: ctx, maia_suggestions: suggestions, last_chat_errors_24h: errs, last_chat_count_24h: chatCount } = data;

  return (
    <div className="max-w-5xl mx-auto py-6 px-3 sm:px-6 space-y-6" data-testid="admin-diag-page">
      <button
        type="button"
        onClick={() => navigate('/app/admin')}
        className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-300 hover:underline"
        data-testid="admin-diag-back"
      >
        <ArrowLeft className="h-4 w-4" /> Retour au dashboard
      </button>

      {/* User header */}
      <header className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
        <Avatar className="h-12 w-12">
          <AvatarImage src={user.avatar} />
          <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white">
            {(user.displayName || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{user.displayName}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">user_id: <code>{user.id}</code></p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-gray-400">Complétude profil</p>
          <p className={`text-2xl font-bold ${user.profileCompleteness >= 70 ? 'text-emerald-500' : user.profileCompleteness >= 40 ? 'text-amber-500' : 'text-gray-400'}`}>
            {user.profileCompleteness}%
          </p>
        </div>
      </header>

      {/* MaIA context */}
      <section data-testid="admin-diag-context">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Contexte MaIA injecté</h2>
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
          <pre className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words overflow-auto max-h-80">
            {JSON.stringify(ctx, null, 2)}
          </pre>
        </div>
      </section>

      {/* Suggestions */}
      <section data-testid="admin-diag-suggestions">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-purple-500" />
          Suggestions actuelles ({suggestions.length})
        </h2>
        {suggestions.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Aucune suggestion disponible.</p>
        ) : (
          <ul className="space-y-2">
            {suggestions.map((s, i) => (
              <li
                key={i}
                className="p-3 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 text-sm text-gray-800 dark:text-gray-200"
                data-testid={`admin-diag-suggestion-${i}`}
              >
                {typeof s === 'string' ? s : (s.label || s.text || s.title || JSON.stringify(s))}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Chat activity */}
      <section data-testid="admin-diag-activity" className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 mb-1">
            <MessageSquare className="h-3.5 w-3.5" /> Messages échangés (24h)
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{chatCount}</p>
        </div>
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 mb-1">
            <Activity className="h-3.5 w-3.5" /> Erreurs LLM (24h)
          </div>
          <p className={`text-2xl font-bold ${errs.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{errs.length}</p>
        </div>
      </section>

      {/* Errors */}
      {errs.length > 0 && (
        <section data-testid="admin-diag-errors">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            Détail des erreurs (24h)
          </h2>
          <ul className="space-y-2">
            {errs.map((e, i) => (
              <li
                key={i}
                className="p-3 rounded-lg bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 text-xs"
                data-testid={`admin-diag-error-${i}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-semibold text-red-700 dark:text-red-300">{e.error_type}</span>
                  <span className="text-gray-500">{new Date(e.timestamp).toLocaleString('fr-FR')}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 break-words">{e.error_message}</p>
                <p className="text-[10px] text-gray-400 mt-1">prompt_len: {e.prompt_len} · session: <code>{e.session_id}</code></p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AdminMaiaDiagnostic;
